import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material'
import { AddCircle as AddCircleIcon } from '@mui/icons-material'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import Select from 'components/generic/form/Select'
import Input from 'components/generic/form/Input'
import { getStatesAndProvinces } from 'utils/helpers'
import { useGetCustomerQuery } from 'services/customers'
import { noop } from 'lodash'
import { CUSTOMERS_FILTERS } from 'utils/constants'
import store from 'state/store'
import { showFormModal } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import ContractFreightCost from './ContractFreightCost'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
const FreightDestination = ({ isView, methods, freightCustomerId, setFreightCustomerId, carrierPricePrepend }) => {
  const {
    control,
    setValue,
    getValues,
    formState: { errors, dirtyFields },
    trigger,
  } = methods
  const { enableSalesOrderFreightCosts, enableCountryCodes } = useAppSelector(
    state => state.featureFlags?.variables || {},
  )
  const isDirty = !!Object.keys(dirtyFields).length
  const statesAndProvinces = useMemo(() => getStatesAndProvinces(['United States', 'Canada']), [])
  const [freightDestinationState, setFreightDestinationState] = useState({})
  const [freightDestinations, setFreightDestinations] = useState([])

  const { data: freightCustomerData } = useGetCustomerQuery(
    { customerId: freightCustomerId },
    { refetchOnMountOrArgChange: true, skip: !freightCustomerId },
  )

  const setDestinationValues = newDestination => {
    if (newDestination !== null && newDestination !== undefined && Object.keys(newDestination)?.length) {
      setValue('freightDestination.locationName', newDestination?.name || '', {
        shouldDirty: true,
      })
      setValue('freightDestination.locationId', newDestination?.id || '', {
        shouldDirty: true,
      })
      setValue('freightDestination.address', newDestination?.address1 || '', {
        shouldDirty: true,
      })
      setValue('freightDestination.city', newDestination?.city || '', {
        shouldDirty: true,
      })
      setValue('freightDestination.state', newDestination?.state || '', {
        shouldDirty: true,
      })
      setValue('freightDestination.zip', newDestination?.zip || '', {
        shouldDirty: true,
      })
      if (enableCountryCodes) {
        setValue('freightDestination.country', newDestination?.country || '', {
          shouldDirty: true,
        })
      }
      trigger('freightDestination.customerName')
      trigger('freightDestination.address')
      trigger('freightDestination.city')
    } else {
      setValue('freightDestination.locationId', '', { shouldDirty: true })
      setValue('freightDestination.locationName', '', { shouldDirty: true })
      setValue('freightDestination.address', '', { shouldDirty: true })
      setValue('freightDestination.city', '', { shouldDirty: true })
      setValue('freightDestination.state', '', { shouldDirty: true })
      setValue('freightDestination.zip', '', { shouldDirty: true })
      if (enableCountryCodes) {
        setValue('freightDestination.country', '', { shouldDirty: true })
      }
    }
  }

  const updateFreightCustomer = customer => {
    if (customer) {
      setFreightCustomerId(customer?.id)
      setValue('freightDestination.customerId', customer?.id || '')
      setValue('freightDestination.customerName', customer?.displayName || '')
    } else {
      setDestinationValues({})
      setFreightDestinations([])
    }
  }

  useEffect(() => {
    if (freightCustomerData) {
      setFreightDestinations(freightCustomerData?.locations || [])
      if (isDirty) {
        const defaultShippingLocation = freightCustomerData?.locations?.find(
          location => !!location.isDefaultShippingLocation,
        )
        const initialFreightDestination = defaultShippingLocation || freightCustomerData?.locations[0]
        setDestinationValues(freightDestinationState?.customerId ? freightDestinationState : initialFreightDestination)
        setValue('freightDestination.customerName', freightCustomerData.displayName)
        setValue('freightDestination.customerId', freightCustomerData.id)
        trigger('freightDestination.customerId')
        trigger('freightDestination.customerName')
      }

      if (
        freightCustomerData?.locations?.length &&
        freightDestinations?.length &&
        freightCustomerData?.locations?.length !== freightDestinations?.length
      ) {
        setDestinationValues(freightCustomerData?.locations.at(-1))
      }
    }
  }, [freightCustomerData])

  useEffect(() => {
    if (!freightDestinationState?.id) return
    setDestinationValues(freightDestinationState)
  }, [freightDestinationState])

  const canAdjustDestination = useMemo(
    () => !isView && !!getValues('freightDestination.customerName') && !!getValues('freightDestination.locationName'),
    [getValues('freightDestination')],
  )

  const handleDestinationSelected = (_, value) => {
    setFreightDestinationState(value)
    setDestinationValues(value)
  }

  return (
    <Box p={3}>
      <Stack direction="row" mb={1}>
        <Typography variant="h6" gutterBottom color="textSecondary">
          Freight Destination
        </Typography>
      </Stack>
      <TableContainer>
        <Table size="small" aria-label="Freight Destination">
          <TableHead>
            <TableRow>
              <TableCell>Customer Name</TableCell>
              <TableCell>Location Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Zip</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingRight: 0,
                  minWidth: '200px',
                }}
              >
                <CustomerAutocomplete
                  name="freightDestination.customerName"
                  variant="outlined"
                  control={control}
                  size="small"
                  data-test="freightDestination-customerName"
                  disabled={isView}
                  value={getValues('freightDestination.customerName') || ''}
                  freeSolo={false}
                  onChange={updateFreightCustomer}
                  baseFilter={CUSTOMERS_FILTERS.CONSUMER}
                  error={!!errors.freightDestination?.customerName}
                  helperText={errors?.freightDestination?.customerName ? 'Freight customer is required' : ' '}
                  autocompleteProps={{
                    renderOption: (props, option) => (
                      <li {...props} key={option.id}>
                        {option.displayName}
                      </li>
                    ),
                  }}
                />
                {isView ? null : (
                  <Button
                    variant="text"
                    startIcon={<AddCircleIcon />}
                    sx={{ minWidth: 'unset', display: 'flex', alignSelf: 'flex-start', marginTop: '5px' }}
                    onClick={() =>
                      store.dispatch(
                        showFormModal({
                          formModalName: FORM_MODALS.NEW_CUSTOMER,
                          formModalProps: {
                            type: 'locations',
                            customerId: freightCustomerId,
                          },
                        }),
                      )
                    }
                  />
                )}
              </TableCell>

              <TableCell>
                <Autocomplete
                  sx={{ marginBottom: '22px' }}
                  name="freightDestination.locationName"
                  variant="outlined"
                  size="small"
                  control={control}
                  data-test="freightDestination-locationName"
                  disabled={isView}
                  onChange={handleDestinationSelected}
                  value={getValues('freightDestination') || ''}
                  getOptionLabel={option => option?.locationName || option?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.name === value?.locationName}
                  options={freightDestinations || []}
                  renderInput={params => <TextField {...params} />}
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id}>
                      {option?.name}
                    </li>
                  )}
                />
              </TableCell>

              <TableCell>
                <Input
                  name="freightDestination.address"
                  variant="outlined"
                  size="small"
                  defaultValue={freightDestinationState?.address || ''}
                  control={control}
                  error={!!errors.freightDestination?.address}
                  disabled={!canAdjustDestination}
                  onBlur={() => trigger('freightDestination.address')}
                  helperText={errors?.freightDestination?.address ? errors.freightDestination.address?.message : ' '}
                />
              </TableCell>
              <TableCell>
                <Input
                  name="freightDestination.city"
                  variant="outlined"
                  size="small"
                  defaultValue={freightDestinationState?.city || ''}
                  control={control}
                  error={!!errors.freightDestination?.city}
                  disabled={!canAdjustDestination}
                  onBlur={() => trigger('freightDestination.city')}
                  helperText={errors?.freightDestination?.city ? errors.freightDestination.city?.message : ' '}
                />
              </TableCell>
              <TableCell>
                <Select
                  name="freightDestination.state"
                  variant="outlined"
                  size="small"
                  defaultValue={freightDestinationState?.state || ''}
                  control={control}
                  items={statesAndProvinces}
                  error={!!errors.freightDestination?.state}
                  disabled={!canAdjustDestination}
                  onBlur={() => trigger('freightDestination.state')}
                  helperText={errors?.freightDestination?.state ? errors.freightDestination.state?.message : ' '}
                />
              </TableCell>
              <TableCell>
                <Input
                  name="freightDestination.zip"
                  variant="outlined"
                  size="small"
                  defaultValue={freightDestinationState?.zip || ''}
                  control={control}
                  error={!!errors.freightDestination?.zip}
                  disabled={!canAdjustDestination}
                  onBlur={() => trigger('freightDestination.zip')}
                  helperText={errors?.freightDestination?.zip ? errors.freightDestination.zip?.message : ' '}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {enableSalesOrderFreightCosts && (
        <ContractFreightCost
          isView={isView}
          methods={methods}
          canAdjustDestination={canAdjustDestination}
          carrierPricePrepend={carrierPricePrepend}
          disabled={!canAdjustDestination}
        />
      )}
    </Box>
  )
}

FreightDestination.propTypes = {
  isView: PropTypes.bool,
  methods: PropTypes.object,
  freightCustomerId: PropTypes.string,
  setFreightCustomerId: PropTypes.func,
  carrierPricePrepend: PropTypes.string,
}

FreightDestination.defaultProps = {
  isView: false,
  methods: null,
  freightCustomerId: '',
  setFreightCustomerId: noop,
  carrierPricePrepend: '$',
}

export default FreightDestination
